import {kmApiLink} from "@pg-mono/km-routes";

import {kmApi} from "@/features/km_app/api/km_api";
import {AppDispatch} from "@/store/types/AppDispatch";

import {closeHomeMatchDetailsModal, finishHomeMatchRecommendationsRefresh, startHomeMatchRecommendationsRefresh} from "../state/home_match_slice";
import {HomeMatchSurveyDto, IHomeMatchSurveyApiRequest} from "../types/home_match_preferences";
import {HomeMatchPreleadStatus} from "../types/HomeMatchPrelead";
import {toHomeMatchFloorNumbers} from "../utils/home_match_floors_preference";
import {IGetHomeMatchPreleadListParams} from "../utils/merge_home_match_prelead_list";
import {getHomeMatchPreferencesApi} from "./get_home_match_preferences";
import {getHomeMatchPreleadListApi} from "./get_home_match_prelead_list";

const REPLACE_PRELEAD_LIST_ARGS = {allowResultsMerge: false} as const;
const REPLACE_PRELEAD_LIST_REFETCH = {forceRefetch: true} as const;
const PENDING_PRELEAD_LIST_ARGS = {...REPLACE_PRELEAD_LIST_ARGS, status: HomeMatchPreleadStatus.PENDING} as const;

let preleadListReplaceGeneration = 0;

const isAbortError = (error: unknown) => typeof error === "object" && error !== null && "name" in error && error.name === "AbortError";

const savedFloors = (preferences: IHomeMatchSurveyApiRequest) => {
    const hasNumbers = "floor_numbers" in preferences;
    const hasPreference = "floors_preference" in preferences;

    if (!hasNumbers && !hasPreference) return null;

    if (preferences.floors_preference) {
        return {
            floor_numbers: toHomeMatchFloorNumbers([preferences.floors_preference]),
            floors_preference: preferences.floors_preference
        };
    }

    if (hasNumbers && preferences.floor_numbers != null) {
        return {
            floor_numbers: preferences.floor_numbers,
            floors_preference: null
        };
    }

    return {floor_numbers: null, floors_preference: null};
};

const writeSavedPreferences = (draft: HomeMatchSurveyDto, preferences: IHomeMatchSurveyApiRequest) => {
    draft.price = preferences.price;
    draft.regions = preferences.regions;
    draft.rooms = preferences.rooms;

    if (preferences.financing_preference) draft.financing_preference = preferences.financing_preference;
    if (preferences.financing_services != null) draft.financing_services = preferences.financing_services;
    if ("additional_areas" in preferences) draft.additional_areas = preferences.additional_areas ?? null;
    if ("area" in preferences) draft.area = preferences.area ?? null;
    if ("construction_end_date" in preferences) draft.construction_end_date = preferences.construction_end_date ?? null;
    const floors = savedFloors(preferences);

    if (floors) {
        draft.floor_numbers = floors.floor_numbers;
        draft.floors_preference = floors.floors_preference;
    }
    if ("high_rise_buildings" in preferences) draft.high_rise_buildings = preferences.high_rise_buildings ?? null;
    if ("low_rise_buildings" in preferences) draft.low_rise_buildings = preferences.low_rise_buildings ?? null;
    if ("market_types" in preferences) draft.market_types = preferences.market_types ?? null;
    if ("types" in preferences) draft.types = preferences.types ?? null;
};

// An empty survey is cached as null, which RTK replaces with the recipe's return value.
const savedPreferencesFromEmptyCache = (preferences: IHomeMatchSurveyApiRequest): HomeMatchSurveyDto => ({
    id: 0,
    additional_areas: preferences.additional_areas ?? null,
    area: preferences.area ?? null,
    financing_services: preferences.financing_services ?? false,
    loan_installment: null,
    price: preferences.price,
    regions: preferences.regions,
    rooms: preferences.rooms,
    types: preferences.types ?? null,
    ...("construction_end_date" in preferences ? {construction_end_date: preferences.construction_end_date ?? null} : {}),
    ...(savedFloors(preferences) ?? {}),
    ...("high_rise_buildings" in preferences ? {high_rise_buildings: preferences.high_rise_buildings ?? null} : {}),
    ...("low_rise_buildings" in preferences ? {low_rise_buildings: preferences.low_rise_buildings ?? null} : {}),
    distance: null,
    construction_end_years: [],
    purchase_purposes: null,
    market_types: preferences.market_types ?? null,
    create_date: "",
    update_date: "",
    ...(preferences.financing_preference ? {financing_preference: preferences.financing_preference} : {})
});

// Chip labels read this cache. Write the saved fields now, before the preferences refetch returns.
const applySavedPreferencesToCache = (dispatch: AppDispatch, preferences: IHomeMatchSurveyApiRequest) => {
    dispatch(
        getHomeMatchPreferencesApi.util.updateQueryData("getHomeMatchPreferences", undefined, (draft) => {
            if (!draft) return savedPreferencesFromEmptyCache(preferences);

            writeSavedPreferences(draft, preferences);
        })
    );
};

// The previous step's list request would otherwise finish last and keep its recommendations.
const abortInFlightPreleadList = async (dispatch: AppDispatch, args: IGetHomeMatchPreleadListParams) => {
    const running = dispatch(getHomeMatchPreleadListApi.util.getRunningQueryThunk("getHomeMatchPreleadList", args));

    if (!running) return;

    running.abort();
    await running;
    // Let RTK drop the aborted request before the next one starts.
    await Promise.resolve();
};

const replaceHomeMatchPreleadListCache = async (dispatch: AppDispatch) => {
    const generation = ++preleadListReplaceGeneration;

    // Overlap only while these args keep different serialized keys. The drop-before-initiate tick stays in abortInFlightPreleadList.
    await Promise.all([abortInFlightPreleadList(dispatch, PENDING_PRELEAD_LIST_ARGS), abortInFlightPreleadList(dispatch, REPLACE_PRELEAD_LIST_ARGS)]);

    if (generation !== preleadListReplaceGeneration) return;

    const {getHomeMatchPreleadList} = getHomeMatchPreleadListApi.endpoints;
    const pendingPreleads = dispatch(getHomeMatchPreleadList.initiate(PENDING_PRELEAD_LIST_ARGS, REPLACE_PRELEAD_LIST_REFETCH));
    const allPreleads = dispatch(getHomeMatchPreleadList.initiate(REPLACE_PRELEAD_LIST_ARGS, REPLACE_PRELEAD_LIST_REFETCH));

    if (generation !== preleadListReplaceGeneration) {
        pendingPreleads.abort();
        allPreleads.abort();
        pendingPreleads.unsubscribe();
        allPreleads.unsubscribe();

        return;
    }

    try {
        await Promise.all([pendingPreleads, allPreleads]);
    } catch (error) {
        if (!isAbortError(error)) throw error;
    } finally {
        pendingPreleads.unsubscribe();
        allPreleads.unsubscribe();
    }
};

export const patchHomeMatchPreferencesApi = kmApi.injectEndpoints({
    endpoints: (builder) => ({
        patchHomeMatchPreferences: builder.mutation<HomeMatchSurveyDto, IHomeMatchSurveyApiRequest>({
            query: (preferences) => ({
                url: kmApiLink.survey(),
                method: "PATCH",
                body: preferences
            }),
            async onQueryStarted(preferences, {dispatch, queryFulfilled}) {
                dispatch(startHomeMatchRecommendationsRefresh());

                try {
                    await queryFulfilled;
                } catch {
                    dispatch(finishHomeMatchRecommendationsRefresh());
                    return;
                }

                try {
                    applySavedPreferencesToCache(dispatch, preferences);
                    dispatch(closeHomeMatchDetailsModal());
                    await replaceHomeMatchPreleadListCache(dispatch);
                } finally {
                    dispatch(finishHomeMatchRecommendationsRefresh());
                }
            },
            // Do not invalidate PreleadList: that refetch merge-appends and would restore the old
            // pending pool. Replace is replaceHomeMatchPreleadListCache. pending_accepted / rejected
            // lists use different tags and were not invalidated on survey save before this change.
            invalidatesTags: ["HomeMatchPreferences", "RegionPriceStats"]
        })
    })
});

export const {usePatchHomeMatchPreferencesMutation} = patchHomeMatchPreferencesApi;
import {kmApiLink} from "@pg-mono/km-routes";

import {kmApi} from "@/features/km_app/api/km_api";
import {AppDispatch} from "@/store/types/AppDispatch";

import {closeHomeMatchDetailsModal, finishHomeMatchRecommendationsRefresh, startHomeMatchRecommendationsRefresh} from "../state/home_match_slice";
import {HomeMatchSurveyDto, IHomeMatchSurveyApiRequest} from "../types/home_match_preferences";
import {HomeMatchPreleadStatus} from "../types/HomeMatchPrelead";
import {toHomeMatchFloorNumbers} from "../utils/home_match_floors_preference";
import {IGetHomeMatchPreleadListParams} from "../utils/merge_home_match_prelead_list";
import {getHomeMatchPreferencesApi} from "./get_home_match_preferences";
import {getHomeMatchPreleadListApi} from "./get_home_match_prelead_list";

const REPLACE_PRELEAD_LIST_ARGS = {allowResultsMerge: false} as const;
const REPLACE_PRELEAD_LIST_REFETCH = {forceRefetch: true} as const;
const PENDING_PRELEAD_LIST_ARGS = {...REPLACE_PRELEAD_LIST_ARGS, status: HomeMatchPreleadStatus.PENDING} as const;

let preleadListReplaceGeneration = 0;

const isAbortError = (error: unknown) => typeof error === "object" && error !== null && "name" in error && error.name === "AbortError";

const savedFloors = (preferences: IHomeMatchSurveyApiRequest) => {
    const hasNumbers = "floor_numbers" in preferences;
    const hasPreference = "floors_preference" in preferences;

    if (!hasNumbers && !hasPreference) return null;

    if (preferences.floors_preference) {
        return {
            floor_numbers: toHomeMatchFloorNumbers([preferences.floors_preference]),
            floors_preference: preferences.floors_preference
        };
    }

    if (hasNumbers && preferences.floor_numbers != null) {
        return {
            floor_numbers: preferences.floor_numbers,
            floors_preference: null
        };
    }

    return {floor_numbers: null, floors_preference: null};
};

const writeSavedPreferences = (draft: HomeMatchSurveyDto, preferences: IHomeMatchSurveyApiRequest) => {
    draft.price = preferences.price;
    draft.regions = preferences.regions;
    draft.rooms = preferences.rooms;

    if (preferences.financing_preference) draft.financing_preference = preferences.financing_preference;
    if (preferences.financing_services != null) draft.financing_services = preferences.financing_services;
    if ("additional_areas" in preferences) draft.additional_areas = preferences.additional_areas ?? null;
    if ("area" in preferences) draft.area = preferences.area ?? null;
    if ("construction_end_date" in preferences) draft.construction_end_date = preferences.construction_end_date ?? null;
    const floors = savedFloors(preferences);

    if (floors) {
        draft.floor_numbers = floors.floor_numbers;
        draft.floors_preference = floors.floors_preference;
    }
    if ("high_rise_buildings" in preferences) draft.high_rise_buildings = preferences.high_rise_buildings ?? null;
    if ("low_rise_buildings" in preferences) draft.low_rise_buildings = preferences.low_rise_buildings ?? null;
    if ("market_types" in preferences) draft.market_types = preferences.market_types ?? null;
    if ("types" in preferences) draft.types = preferences.types ?? null;
};

// An empty survey is cached as null, which RTK replaces with the recipe's return value.
const savedPreferencesFromEmptyCache = (preferences: IHomeMatchSurveyApiRequest): HomeMatchSurveyDto => ({
    id: 0,
    additional_areas: preferences.additional_areas ?? null,
    area: preferences.area ?? null,
    financing_services: preferences.financing_services ?? false,
    loan_installment: null,
    price: preferences.price,
    regions: preferences.regions,
    rooms: preferences.rooms,
    types: preferences.types ?? null,
    ...("construction_end_date" in preferences ? {construction_end_date: preferences.construction_end_date ?? null} : {}),
    ...(savedFloors(preferences) ?? {}),
    ...("high_rise_buildings" in preferences ? {high_rise_buildings: preferences.high_rise_buildings ?? null} : {}),
    ...("low_rise_buildings" in preferences ? {low_rise_buildings: preferences.low_rise_buildings ?? null} : {}),
    distance: null,
    construction_end_years: [],
    purchase_purposes: null,
    market_types: preferences.market_types ?? null,
    create_date: "",
    update_date: "",
    ...(preferences.financing_preference ? {financing_preference: preferences.financing_preference} : {})
});

// Chip labels read this cache. Write the saved fields now, before the preferences refetch returns.
const applySavedPreferencesToCache = (dispatch: AppDispatch, preferences: IHomeMatchSurveyApiRequest) => {
    dispatch(
        getHomeMatchPreferencesApi.util.updateQueryData("getHomeMatchPreferences", undefined, (draft) => {
            if (!draft) return savedPreferencesFromEmptyCache(preferences);

            writeSavedPreferences(draft, preferences);
        })
    );
};

// The previous step's list request would otherwise finish last and keep its recommendations.
const abortInFlightPreleadList = async (dispatch: AppDispatch, args: IGetHomeMatchPreleadListParams) => {
    const running = dispatch(getHomeMatchPreleadListApi.util.getRunningQueryThunk("getHomeMatchPreleadList", args));

    if (!running) return;

    running.abort();
    await running;
    // Let RTK drop the aborted request before the next one starts.
    await Promise.resolve();
};

const replaceHomeMatchPreleadListCache = async (dispatch: AppDispatch) => {
    const generation = ++preleadListReplaceGeneration;

    // Overlap only while these args keep different serialized keys. The drop-before-initiate tick stays in abortInFlightPreleadList.
    await Promise.all([abortInFlightPreleadList(dispatch, PENDING_PRELEAD_LIST_ARGS), abortInFlightPreleadList(dispatch, REPLACE_PRELEAD_LIST_ARGS)]);

    if (generation !== preleadListReplaceGeneration) return;

    const {getHomeMatchPreleadList} = getHomeMatchPreleadListApi.endpoints;
    const pendingPreleads = dispatch(getHomeMatchPreleadList.initiate(PENDING_PRELEAD_LIST_ARGS, REPLACE_PRELEAD_LIST_REFETCH));
    const allPreleads = dispatch(getHomeMatchPreleadList.initiate(REPLACE_PRELEAD_LIST_ARGS, REPLACE_PRELEAD_LIST_REFETCH));

    if (generation !== preleadListReplaceGeneration) {
        pendingPreleads.abort();
        allPreleads.abort();
        pendingPreleads.unsubscribe();
        allPreleads.unsubscribe();

        return;
    }

    try {
        await Promise.all([pendingPreleads, allPreleads]);
    } catch (error) {
        if (!isAbortError(error)) throw error;
    } finally {
        pendingPreleads.unsubscribe();
        allPreleads.unsubscribe();
    }
};

export const patchHomeMatchPreferencesApi = kmApi.injectEndpoints({
    endpoints: (builder) => ({
        patchHomeMatchPreferences: builder.mutation<HomeMatchSurveyDto, IHomeMatchSurveyApiRequest>({
            query: (preferences) => ({
                url: kmApiLink.survey(),
                method: "PATCH",
                body: preferences
            }),
            async onQueryStarted(preferences, {dispatch, queryFulfilled}) {
                dispatch(startHomeMatchRecommendationsRefresh());

                try {
                    await queryFulfilled;
                } catch {
                    dispatch(finishHomeMatchRecommendationsRefresh());
                    return;
                }

                try {
                    applySavedPreferencesToCache(dispatch, preferences);
                    dispatch(closeHomeMatchDetailsModal());
                    await replaceHomeMatchPreleadListCache(dispatch);
                } finally {
                    dispatch(finishHomeMatchRecommendationsRefresh());
                }
            },
            // Do not invalidate PreleadList: that refetch merge-appends and would restore the old
            // pending pool. Replace is replaceHomeMatchPreleadListCache. pending_accepted / rejected
            // lists use different tags and were not invalidated on survey save before this change.
            invalidatesTags: ["HomeMatchPreferences", "RegionPriceStats"]
        })
    })
});

export const {usePatchHomeMatchPreferencesMutation} = patchHomeMatchPreferencesApi;
